import type { ReactNode, Ref } from 'react';
import React from 'react';
import clsx from 'clsx';
import { NextFutureImage } from '@components/main-components/image/NextFutureImage';
import { blurImage } from '@components/main-components/image/blur-image';
import { AccessibleLink } from '@components/main-components/link/AccessibleLink';
import { PlayButton } from '@components/main-components/play-button/PlayButton';
import type { CurrentListeningParty } from '@hooks/usePlayButton';
import { useSongCardContext } from './Context';
import { AlbumLabel } from './labels/AlbumLabel';

const ASSUMED_IMAGE_SIZE = 200;

const responsiveImageStyle: React.CSSProperties = {
  width: '100%',
  height: '100%',
  aspectRatio: '1/1',
};

/**
 * fill array with empty string to make sure we always have 3 images
 */
const fillEmptyImages = (images: string[]) => {
  const emptyImages = Array.from({ length: 3 - images.length }, () => '');
  return [...emptyImages, ...images];
};

const blurPlaceholder = blurImage(ASSUMED_IMAGE_SIZE, ASSUMED_IMAGE_SIZE);

const MultipleCoverImages = ({
  images,
  unoptimized,
}: {
  images: string[];
  /**
   * MAKE SURE YOU KNOW WHAT YOU ARE DOING.
   *
   * This was added so that we can render the image in storybook preview.
   * Only scenario you would want to use this is if you are trying to render GIFs
   *
   * But if you are thinking to render GIFs would HIGHLY recommend consulting with the team first.
   */
  unoptimized?: boolean;
}) => {
  return (
    <div className="absolute inset-0 bg-black [&_.imageIndex1]:-bottom-1/2 [&_.imageIndex1]:left-[-20%] [&_.imageIndex1]:rotate-[-30deg] [&_.imageIndex2]:left-8 [&_.imageIndex2]:top-16 [&_.imageIndex2]:rotate-[-15deg] [&_.imageIndex3]:left-[20%] [&_.imageIndex3]:top-[28%] [&_.imageIndex3]:rotate-[30deg] [&_img]:absolute [&_img]:max-h-[90%] [&_img]:max-w-[90%] [&_img]:overflow-hidden [&_img]:rounded-md [&_img]:shadow-lg">
      {/* we only show at max 3 images  */}
      {fillEmptyImages(images)
        .slice(0, 3)
        .map((image, index) => {
          if (image === '') return <div key={index} className={`imageIndex${index + 1}`} />;

          return (
            <NextFutureImage
              src={image}
              className={`imageIndex${index + 1}`}
              unoptimized={unoptimized}
              width={ASSUMED_IMAGE_SIZE}
              height={ASSUMED_IMAGE_SIZE}
              alt={`Cover image ${index + 1}`}
              key={image + index}
              // song cards either appear in carousels or grids, which use the useColumns hook to determine the number of columns
              // Based on that we can assume what sizes to use for the images
              sizes="(max-width: 640px) 66vw, 
              (max-width: 768px) 40vw,
              (max-width: 1280px) 33vw,
              25vw"
              style={responsiveImageStyle}
              placeholder="blur"
              blurDataURL={blurPlaceholder}
            />
          );
        })}
    </div>
  );
};

const SingleImage = ({
  src,
  unoptimized,
}: {
  src: string;
  /**
   * MAKE SURE YOU KNOW WHAT YOU ARE DOING.
   *
   * This was added so that we can render the image in storybook preview.
   * Only scenario you would want to use this is if you are trying to render GIFs
   *
   * But if you are thinking to render GIFs would HIGHLY recommend consulting with the team first.
   */
  unoptimized?: boolean;
  /**
   * Priority for the image
   * @default false
   * @see https://nextjs.org/docs/basic-features/image-optimization#lazy-loading
   *
   */
}) => {
  return (
    <NextFutureImage
      src={src}
      className="group-hover:scale-125"
      width={ASSUMED_IMAGE_SIZE}
      height={ASSUMED_IMAGE_SIZE}
      alt="Sound card cover"
      unoptimized={unoptimized}
      // song cards either appear in carousels or grids, which use the useColumns hook to determine the number of columns
      // Based on that we can assume what sizes to use for the images
      sizes="(max-width: 640px) 66vw, 
              (max-width: 768px) 40vw,
              (max-width: 1280px) 33vw,
              25vw"
      style={responsiveImageStyle}
    />
  );
};

export function CoverImage({
  onPlayButtonClick,
  header,
  isBufferingSong,
  coverLink,
  image,
  disablePlay,
  playButtonPrefetchRef,
  currentListeningParty,
}: {
  /**
   * Callback to be called when the play button is clicked
   */
  onPlayButtonClick: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  /**
   * Header component to render on top of the cover image
   */
  header?: React.ReactNode;
  /**
   * loading indicator for the play button
   */
  isBufferingSong?: boolean;
  /**
   * Link to the cover image for navigation
   */
  coverLink?: string | undefined;
  /**
   * To show play button in disabled state
   */
  disablePlay?: boolean;
  /**
   * Image Component to render. React node so that we can add onClick events etc.
   */
  image: ReactNode;

  playButtonPrefetchRef?: Ref<HTMLButtonElement>;

  currentListeningParty: CurrentListeningParty;
}) {
  const { album, isPlaying, expanded } = useSongCardContext();

  return (
    <div className="relative aspect-square overflow-hidden group-hover:[&_img.imageIndex1]:rotate-[-20deg] group-hover:[&_img.imageIndex2]:rotate-[-10deg] group-hover:[&_img.imageIndex3]:rotate-[35deg]">
      {header && <div className="absolute left-0 right-0 top-0 z-above1">{header}</div>}
      <div className="relative h-full overflow-hidden object-contain transition-transform duration-500 ease-in-out [&_img]:object-cover [&_img]:duration-500">
        {coverLink ? (
          <AccessibleLink absoluteUri={coverLink} type="external">
            {image}
          </AccessibleLink>
        ) : (
          image
        )}
      </div>
      <div className="pointer-events-none absolute bottom-0 z-above2 h-28 w-full transform-gpu bg-gradient-to-t from-black" />
      {album && (
        <div className="absolute bottom-1 left-3 z-above2">
          <AlbumLabel color="white" />
        </div>
      )}
      <div
        className={clsx(
          'absolute bottom-0 right-3 z-above3 transform-gpu opacity-100 transition-opacity duration-500 group-hover:opacity-100 xl:opacity-0',
          isPlaying && 'opacity-100 group-hover:opacity-100',
          expanded && 'hidden',
        )}
      >
        <PlayButton
          color="white"
          playing={isPlaying}
          onClick={onPlayButtonClick}
          size="s"
          loading={isBufferingSong}
          disabled={disablePlay}
          ref={playButtonPrefetchRef}
          currentListeningParty={currentListeningParty}
        />
      </div>
    </div>
  );
}

/**
 * To render a image in song card.
 */
CoverImage.Single = SingleImage;
/**
 * To render 3 images in song card.
 */
CoverImage.Multiple = MultipleCoverImages;
